import { useLiveQuery } from 'dexie-react-hooks'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { avenantsDuMarche, creerAvenant, cumulMarche, factureEnVigueur, LIBELLES_STATUT, totauxDevis } from '../lib/devis'
import { formatEuro } from '../lib/money'
import type { BaseOutbox, DevisLocal } from '../outbox/base'
import { Bouton } from './composants/bouton'

/**
 * Marché et avenants d'un devis signé : liste, cumul (marché initial + avenants signés) et création d'un avenant.
 * Les avenants se signent et se facturent à part ; les situations de travaux restent celles du marché initial.
 */
export function BlocAvenants(props: { base: BaseOutbox; marche: DevisLocal }) {
  const { base, marche } = props
  const naviguer = useNavigate()
  const avenants = useLiveQuery(() => avenantsDuMarche(base, marche.id), [marche.id], [])
  const [erreur, setErreur] = useState<string | null>(null)
  const solde = !!factureEnVigueur(marche)
  const cumul = cumulMarche(marche, avenants)

  async function creer() {
    setErreur(null)
    try {
      naviguer(`/devis/${(await creerAvenant(base, marche)).id}`)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Création impossible.')
    }
  }

  return (
    <section className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white shadow-sm p-3" aria-label="Avenants">
      <h3 className="font-semibold">Avenants</h3>
      {avenants.length === 0 && <p className="text-sm text-slate-700">Aucun avenant. Un avenant ajoute des travaux au marché, avec sa propre signature et sa propre facture.</p>}
      <ul className="flex flex-col gap-1">
        {avenants.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-2">
            <Link to={`/devis/${a.id}`} className="min-h-12 flex-1 content-center underline">
              {a.numero} · {LIBELLES_STATUT[a.statut]}
            </Link>
            <span className="shrink-0 text-sm">{formatEuro(totauxDevis(a).ttc)}</span>
          </li>
        ))}
      </ul>
      {avenants.length > 0 && (
        <dl className="grid grid-cols-[1fr_auto] gap-x-3 border-t border-slate-200 pt-2 text-sm">
          <dt>Marché initial</dt>
          <dd className="text-right">{formatEuro(cumul.initial.ttc)}</dd>
          <dt>Avenants signés</dt>
          <dd className="text-right">{formatEuro(cumul.signes.ttc)}</dd>
          {cumul.enAttente.ttc.greaterThan(0) && (
            <>
              <dt className="text-slate-700">Avenants envoyés, à signer</dt>
              <dd className="text-right text-slate-700">{formatEuro(cumul.enAttente.ttc)}</dd>
            </>
          )}
          <dt className="font-bold">Total du marché TTC</dt>
          <dd className="text-right font-bold">{formatEuro(cumul.total.ttc)}</dd>
        </dl>
      )}
      {solde ? (
        <p className="text-sm text-slate-700">Ce devis est soldé par une facture : annulez-la par un avoir pour ajouter un avenant, ou faites un nouveau devis.</p>
      ) : (
        <Bouton variante="secondaire" onClick={creer}>
          Créer un avenant
        </Bouton>
      )}
      {erreur && (
        <p className="text-sm text-red-800" role="alert">
          {erreur}
        </p>
      )}
    </section>
  )
}
